// PRODUCTION — add form items modal
import React, { Fragment, useState } from 'react'
import { createPortal } from 'react-dom'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CollectDataFormItemType } from 'src/constants/collect-data/enums'
import {
  formItemModalCardCss,
  formItemTileCss,
  modalBackdropCss,
  modalCloseBtnCss,
  modalFooterActionsCss,
  modalFooterCss,
  modalFooterHintCss,
  modalHeaderCss,
  modalTitleCss,
} from '../../flowEditorModalStyles'
import { FORM_ITEM_META, FORM_ITEM_TYPES } from './formItemMeta'

export function AddFormItemModal({
  onClose,
  onAdd,
}: {
  onClose: () => void
  onAdd: (types: CollectDataFormItemType[]) => void
}) {
  const [selected, setSelected] = useState<CollectDataFormItemType[]>([])

  const toggle = (type: CollectDataFormItemType) => {
    setSelected((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]))
  }

  const handleConfirm = () => {
    if (selected.length === 0) {
      return
    }

    onAdd(selected)
    onClose()
  }

  let addLabel = 'Add items'

  if (selected.length > 1) {
    addLabel = `Add ${selected.length} items`
  } else if (selected.length === 1) {
    addLabel = 'Add 1 item'
  }

  return createPortal(
    <Fragment>
      <div onClick={onClose} css={modalBackdropCss} />
      <div css={formItemModalCardCss}>
        <div css={modalHeaderCss}>
          <span css={modalTitleCss}>Add Form Items</span>
          <button type="button" onClick={onClose} css={modalCloseBtnCss}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div css={tw`grid max-h-[60vh] grid-cols-3 gap-3 overflow-y-auto px-6 py-5`}>
          {FORM_ITEM_TYPES.map((type) => {
            const m = FORM_ITEM_META[type]
            const isSelected = selected.includes(type)

            return (
              <div key={type} onClick={() => toggle(type)} css={formItemTileCss(isSelected)}>
                <div css={tw`flex items-center gap-2`}>
                  <div
                    css={[
                      tw`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg`,
                      isSelected
                        ? tw`bg-primary-100 text-primary-600`
                        : tw`bg-shades-100 text-shades-500`,
                    ]}
                  >
                    {m.icon}
                  </div>
                  <span
                    css={[
                      tw`text-[13px] font-bold leading-[18px]`,
                      isSelected ? tw`text-primary-700` : tw`text-shades-900`,
                    ]}
                  >
                    {m.label}
                  </span>
                </div>
                <p
                  css={[
                    tw`m-0 text-xs leading-[17px]`,
                    isSelected ? tw`text-primary-600` : tw`text-shades-500`,
                  ]}
                >
                  {m.description}
                </p>
              </div>
            )
          })}
        </div>
        <div css={modalFooterCss}>
          <span css={modalFooterHintCss}>
            {selected.length > 0 ? `${selected.length} selected` : 'Select items to add'}
          </span>
          <div css={modalFooterActionsCss}>
            <Button
              variant="outlined"
              size="small"
              color="white"
              onClick={onClose}
              className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color={selected.length > 0 ? 'primary' : 'grey'}
              onClick={handleConfirm}
              disabled={selected.length === 0}
            >
              {addLabel}
            </Button>
          </div>
        </div>
      </div>
    </Fragment>,
    document.body,
  )
}
